/**
 * Centralized PRODUCT branding - the application vendor identity (GoERP), distinct from the
 * per-tenant company branding that appears inside each workspace, documents and emails.
 * Every product-brand string, logo path and contact detail must come from here - never hardcode.
 */
export const APP_NAME = 'TravelERP';
export const COMPANY_NAME = 'GoERP';

/** Registered legal entity. Used wherever the company must be named formally (footer, invoices, legal). */
export const COMPANY_LEGAL_NAME = 'GoERP Technologies Pvt. Ltd.';

export const COMPANY_WEBSITE = 'https://goerp.live';
export const COMPANY_EMAIL = 'info@goerp.live';

/** Single number for both voice and WhatsApp. */
export const COMPANY_PHONE = '+91 9828996926';

/** Registered office, split so the UI can lay it out over multiple lines without re-parsing a string. */
export const COMPANY_ADDRESS = {
  line1: 'B-212, Pathik Nagar',
  city: 'Bhilwara',
  postalCode: '311001',
  state: 'Rajasthan',
  country: 'INDIA',
} as const;

/** One-line form for compact places (contact rows, meta tags, email signatures). */
export const COMPANY_ADDRESS_LINE = `${COMPANY_ADDRESS.line1}, ${COMPANY_ADDRESS.city} - ${COMPANY_ADDRESS.postalCode}, ${COMPANY_ADDRESS.state}, ${COMPANY_ADDRESS.country}`;

/**
 * Logo assets.
 *
 * `logo` carries the wordmark in WHITE, so it only reads on a dark surface. `icon` is the mark alone
 * and is safe on any background - prefer it wherever the surface colour is not guaranteed.
 *
 * `logoFull` is the complete lockup as supplied by the brand - cloud, wordmark and the TM together,
 * on transparency. Use it where the logo is the hero of the screen, so the artwork stays the brand's
 * own rather than something reassembled here out of an SVG mark plus a typed-out wordmark.
 */
export const COMPANY_LOGO = '/logo-light.png';
export const COMPANY_ICON = '/goerp-logo.svg';
export const COMPANY_LOGO_FULL = '/goerp-logo-full.png';

/**
 * The attribution shown at the bottom of public-facing screens.
 *
 * The year is FIXED, not `new Date().getFullYear()`: this line states who owns the product, and a
 * year that silently rolls over on 1 January is a claim nobody reviewed. The separate copyright line
 * in the footer still tracks the current year.
 */
export const POWERED_BY_YEAR = 2026;
export const POWERED_BY = `Powered by ${POWERED_BY_YEAR} ${COMPANY_LEGAL_NAME}`;

/** One-line positioning shown beside the wordmark on onboarding and other full-screen product surfaces. */
export const PRODUCT_TAGLINE = 'AI-Powered Travel ERP';

/** The full wordmark as it is set in the logo lockup, including the domain suffix. */
export const PRODUCT_WORDMARK = 'GoERP.live';

/** Capability labels shown under the logo lockup on the onboarding welcome screen. */
export const PRODUCT_CAPABILITIES = ['TRAVEL', 'ERP', 'QUOTATIONS', 'OPERATIONS', 'GROWTH'] as const;

export const PRODUCT_BRAND = {
  name: APP_NAME,
  shortName: COMPANY_NAME,
  tagline: PRODUCT_TAGLINE,
  wordmark: PRODUCT_WORDMARK,
  capabilities: PRODUCT_CAPABILITIES,
  legalName: COMPANY_LEGAL_NAME,
  logo: COMPANY_LOGO,
  logoFull: COMPANY_LOGO_FULL,
  icon: COMPANY_ICON,
  favicon: '/favicon.svg',
  website: COMPANY_WEBSITE,
  email: COMPANY_EMAIL,
  phone: COMPANY_PHONE,
  whatsapp: COMPANY_PHONE,
  address: COMPANY_ADDRESS,
  addressLine: COMPANY_ADDRESS_LINE,
  poweredBy: POWERED_BY,
  poweredByYear: POWERED_BY_YEAR,
} as const;
